import { Button, pantryColors, pantryTypography } from '@pantry/ui'
import { Formik } from 'formik'
import React, { useEffect, useRef } from 'react'
import * as Yup from 'yup'

const p = pantryColors
const F = pantryTypography.fontFamily

export interface AddTagFormValues {
  name: string
}

interface AddTagDialogProps {
  open: boolean
  isSaving: boolean
  onClose: () => void
  onCreate: (values: AddTagFormValues) => void
}

const schema = Yup.object({
  name: Yup.string().trim().required('Please enter a tag name'),
})

const initialValues: AddTagFormValues = {
  name: '',
}

export const AddTagDialog: React.FC<AddTagDialogProps> = ({
  open,
  isSaving,
  onClose,
  onCreate,
}) => {
  const nameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => nameInputRef.current?.focus(), 80)
    return () => clearTimeout(t)
  }, [open])

  if (!open) return null

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      enableReinitialize
      onSubmit={(values) => onCreate(values)}
    >
      {({ values, errors, touched, handleChange, submitForm }) => {
        const showNameError = touched.name && !!errors.name

        return (
          <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
              <div style={headerStyle}>
                <div style={iconWrapStyle}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ width: 14, height: 14 }}
                  >
                    <path
                      d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                </div>
                <h3 style={titleStyle}>New Tag</h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  style={closeBtnStyle}
                >
                  ×
                </button>
              </div>

              <div style={bodyStyle}>
                <div style={alertStyle}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      width: 12,
                      height: 12,
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>
                    The <strong>Tag Name</strong> is what customers see in
                    filters and carousels. Edit the color later from Tag
                    management.
                  </span>
                </div>

                <div style={fieldWrapStyle}>
                  <label htmlFor="add-tag-name" style={labelStyle}>
                    Tag Name
                  </label>
                  <input
                    id="add-tag-name"
                    ref={nameInputRef}
                    name="name"
                    type="text"
                    value={values.name}
                    onChange={handleChange}
                    placeholder="e.g. Staff Pick"
                    style={inputStyle}
                  />
                  {showNameError ? (
                    <div style={errorStyle}>{errors.name}</div>
                  ) : null}
                </div>
              </div>

              <div style={footerStyle}>
                <Button variant="ghost" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={submitForm}
                  disabled={isSaving || !values.name.trim()}
                >
                  {isSaving ? 'Adding…' : 'Add Tag'}
                </Button>
              </div>
            </div>
          </div>
        )
      }}
    </Formik>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
}

const modalStyle: React.CSSProperties = {
  background: p.white,
  borderRadius: 12,
  width: 400,
  maxWidth: 'calc(100vw - 32px)',
  boxShadow: '0 20px 60px rgba(0,0,0,.25)',
  overflow: 'hidden',
  fontFamily: F,
}

const headerStyle: React.CSSProperties = {
  padding: '14px 18px',
  borderBottom: `1px solid ${p.gray[200]}`,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}

const iconWrapStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: p.brand.light,
  color: p.brand.primary,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
}

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: p.gray[800],
  flex: 1,
  margin: 0,
}

const closeBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: p.gray[400],
  fontSize: 18,
  lineHeight: 1,
  padding: '2px 6px',
  fontFamily: F,
}

const bodyStyle: React.CSSProperties = {
  padding: '16px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
}

const alertStyle: React.CSSProperties = {
  background: p.amber[100],
  border: `1px solid ${p.amber[200]}`,
  borderRadius: 6,
  padding: '9px 12px',
  fontSize: 11.5,
  color: p.amber[800],
  display: 'flex',
  gap: 7,
  alignItems: 'flex-start',
}

const fieldWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: p.gray[600],
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 11px',
  border: `1px solid ${p.gray[300]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[800],
  outline: 'none',
  fontFamily: F,
}

const footerStyle: React.CSSProperties = {
  padding: '12px 18px',
  borderTop: `1px solid ${p.gray[200]}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
}

const errorStyle: React.CSSProperties = {
  color: p.red[700],
  fontSize: 12,
}
